import React from 'react';
import { Box, Stack, Button, Typography } from '@mui/material';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';

import {
  buttonStyles,
  dividerStyles,
} from '../../../modals/unified_config_modals/styles/unifiedConfigStyles';
import { useTranslation } from 'react-i18next';
import { StringKeys } from '@/resources/strings/StringKeys';
import SensitivityChip from '../../../shared/components/SensitivityChip';

const TOTAL_CHIPS = 22;
const CHIPS_PER_ROW = 11;

interface Props {
  values: boolean[];
  onToggle: (index: number) => void;
  smartAdaptive: boolean;
  setSmartAdaptive: (v: boolean) => void;
  onEnableAll: () => void;
  onDisableAll: () => void;
  disabled?: boolean;
  readonly?: boolean;
  onChipHover?: (sensitivity: number) => void;
  onChipLeave?: () => void;
}

const Flat22SensitivityField: React.FC<Props> = ({
  values,
  onToggle,
  smartAdaptive,
  setSmartAdaptive,
  onEnableAll,
  onDisableAll,
  disabled = false,
  readonly = false,
  onChipHover,
  onChipLeave,
}) => {
  const { t } = useTranslation();

  if (values.length !== TOTAL_CHIPS) {
    console.error(
      `Flat22SensitivityField expected ${TOTAL_CHIPS} chips, got ${values.length}`,
    );
  }

  const renderChip = (selected: boolean, i: number) => (
    <SensitivityChip
      key={i}
      label={i + 1}
      selected={selected}
      disabled={disabled}
      readonly={readonly}
      onClick={() => onToggle(i)}
      onMouseEnter={() => onChipHover?.(i + 1)}
      onMouseLeave={() => onChipLeave?.()}
    />
  );

  const row1 = values.slice(0, CHIPS_PER_ROW);
  const row2 = values.slice(CHIPS_PER_ROW);

  const saChip = (
    <SensitivityChip
      label={t(StringKeys.ADAPTIVE_MODES)}
      selected={smartAdaptive}
      variant="smart-adaptive"
      disabled={disabled}
      readonly={readonly}
      onClick={() => setSmartAdaptive(!smartAdaptive)}
    />
  );

  return (
    <Box sx={{ mb: 4 }}>
      {/* Desktop: two fixed rows of 11, SA chip vertically centred alongside (wraps to third row at 1200–1350px) */}
      <Box sx={{ display: { xs: 'none', md: 'flex' }, alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
        <Box
          sx={{
            '@media (min-width: 1200px) and (max-width: 1350px)': { width: '100%' },
          }}
        >
          <Stack direction="row" flexWrap="nowrap" gap={1} alignItems="center" mb={1}>
            {row1.map((v, i) => renderChip(v, i))}
          </Stack>
          <Stack direction="row" flexWrap="nowrap" gap={1} alignItems="center">
            {row2.map((v, i) => renderChip(v, i + CHIPS_PER_ROW))}
          </Stack>
        </Box>
        <Box
          sx={{
            ...dividerStyles.vertical(disabled || readonly),
            '@media (min-width: 1200px) and (max-width: 1350px)': { display: 'none' },
          }}
        />
        {saChip}
      </Box>

      {/* Mobile: single flat wrapping row */}
      <Stack
        direction="row"
        flexWrap="wrap"
        gap={1}
        alignItems="center"
        sx={{ display: { xs: 'flex', md: 'none' } }}
      >
        {values.map((v, i) => renderChip(v, i))}
        <Box sx={dividerStyles.vertical(disabled || readonly)} />
        {saChip}
      </Stack>

      {!readonly && (
        <Stack direction="row" spacing={1} mt={2}>
          <Button
            variant="contained"
            size="small"
            onClick={() => {
              if (!disabled) {
                onEnableAll();
                setSmartAdaptive(true);
              }
            }}
            sx={buttonStyles.enableAll}
          >
            {t(StringKeys.ENABLE_ALL)}
          </Button>
          <Button
            variant="outlined"
            size="small"
            onClick={() => {
              if (!disabled) {
                onDisableAll();
                setSmartAdaptive(false);
              }
            }}
            sx={buttonStyles.disableAll}
          >
            {t(StringKeys.DISABLE_ALL)}
          </Button>
        </Stack>
      )}

      {!readonly && !smartAdaptive && (
        <Stack direction="row" alignItems="center" spacing={0.5} mt={1.5}>
          <InfoOutlinedIcon fontSize="small" sx={{ color: 'text.secondary', fontSize: 16 }} />
          <Typography variant="caption" sx={{ color: 'text.secondary' }}>
            {t(StringKeys.ENABLE_SMART_ADAPTIVE_FOR_ADVANCED_SETTINGS)}
          </Typography>
        </Stack>
      )}
    </Box>
  );
};

export default Flat22SensitivityField;
